/**
 * Keeps images blank while they stream in, then fades them in once fully
 * loaded — no progressive top-to-bottom paint and no alt text flashing in
 * the empty slot (see the img rules in base.css).
 *
 * Only images that are still loading get marked, so nothing stays hidden
 * if this script fails to run.
 */
(() => {
  const MARKED = 'image-reveal';
  const LOADED = 'image-reveal--loaded';

  /** @param {HTMLImageElement} img */
  const mark = (img) => {
    if (!img.complete && !img.classList.contains(MARKED)) {
      img.classList.add(MARKED);
    }
  };

  /** @param {Event} event */
  const reveal = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLImageElement) || !target.classList.contains(MARKED)) return;
    // Swap the hiding class for the transition-only class so opacity animates
    // from 0 back to whatever the stylesheet gives this image, then drop the
    // transition class once the fade is over to leave no styling footprint.
    target.classList.add(LOADED);
    target.classList.remove(MARKED);
    setTimeout(() => target.classList.remove(LOADED), 600);
  };

  // load/error don't bubble, so listen during the capture phase. Errored
  // images are revealed too — a hidden broken image is worse than the icon.
  document.addEventListener('load', reveal, true);
  document.addEventListener('error', reveal, true);

  document.querySelectorAll('img').forEach(mark);

  // Mark images added later (variant switches, quick add, section rendering).
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLImageElement) {
          mark(node);
        } else if (node instanceof Element) {
          node.querySelectorAll('img').forEach(mark);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Safety net for images that finished without firing a caught load event,
  // e.g. bfcache restores: unhide immediately, no fade needed.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll(`img.${MARKED}`).forEach((img) => {
      if (img instanceof HTMLImageElement && img.complete) {
        img.classList.remove(MARKED);
      }
    });
  });
})();
